/**
 * The KPI cards under the supplier header: Performance Rating (graded on the
 * configured bands) and Total Orders. Which ones show is a supplier-profile
 * layout setting. CR-5767 replaced the old OTD card with the rating, which
 * is why the rating card still hangs off the layout's `otd` flag.
 *
 * Split out of SupplierDetail.tsx to keep that page under the 500-line
 * limit; the markup is unchanged.
 */
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { Award, Info, Package } from 'lucide-react';
import type { SupplierProfile } from '../../lib/api';
import { getGradeInfo, ratingBadgeStyle, bandRange } from '../supplier-performance/utils';
import { useRatingBands } from '../../hooks/useRatingBands';

interface SupplierKpiCardsProps {
  supplier: SupplierProfile;
  showRating: boolean;
  showTotalOrders: boolean;
}

export function SupplierKpiCards({ supplier, showRating, showTotalOrders }: SupplierKpiCardsProps) {
  // Customer review 11 Sep (item 2): the configured Grade Thresholds.
  const { bands: ratingBands } = useRatingBands();

  if (!showRating && !showTotalOrders) return null;

  const score = supplier.combined_score;
  const hasScore = typeof score === 'number' && !isNaN(score);
  const gradeInfo = getGradeInfo(score, ratingBands);

  return (
    <div className={`grid gap-4 ${showRating && showTotalOrders ? 'md:grid-cols-2' : 'md:grid-cols-1'}`}>
      {showRating && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Award className="h-4 w-4" />
              Performance Rating
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3.5 w-3.5 text-muted-foreground/70 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs text-xs p-3">
                    <p className="font-semibold mb-1">Rating Calculation</p>
                    <p className="mb-2">Combined Score = (OTIF Score × 70%) + (PPM Score × 30%)</p>
                    <p className="font-semibold mb-1">Rating Tiers</p>
                    {/* Customer review 11 Sep (msg 2): the configured bands,
                        not a hard-coded Gold/Silver/Bronze list. */}
                    <ul className="space-y-0.5">
                      {ratingBands.map((b) => (
                        <li key={b.id}>• {b.display_label || b.label || b.grade}: {bandRange(b)}</li>
                      ))}
                    </ul>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {hasScore ? (
              <Badge
                className="text-xl font-bold px-3 py-1"
                style={{
                  ...ratingBadgeStyle(gradeInfo.color),
                }}
              >
                {gradeInfo.label}
              </Badge>
            ) : (
              <div className="text-3xl font-bold text-muted-foreground">N/A</div>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              Combined Score: {hasScore ? score.toFixed(1) : '-'}
            </p>
          </CardContent>
        </Card>
      )}
      {showTotalOrders && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Package className="h-4 w-4" />
              Total Orders
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{supplier.total_orders}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {supplier.completed_orders} completed
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
